import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useResponsive } from '../hooks/useResponsive';
import { supabase } from '../lib/supabaseClient';
import DashboardNavigation, { AvatarMenu } from '../components/DashboardNavigation';
import MobileHeaderDropdown from '../components/MobileHeaderDropdown';
import ImpersonationBanner from '../components/ImpersonationBanner';
import Footer from '../components/Footer';

const AccountInfoPage = () => {
  const navigate = useNavigate();
  const { user, profile, getDefaultDashboard } = useAuth();
  const { isDesktop } = useResponsive();
  const [familyInfo, setFamilyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [defaultDashboard, setDefaultDashboard] = useState('');
  const [savingPreference, setSavingPreference] = useState(false);
  const [preferenceSuccess, setPreferenceSuccess] = useState('');

  useEffect(() => {
    if (profile?.id) {
      loadFamilyInfo();
      // Set initial default dashboard from profile or role-based default
      const initial = profile.default_dashboard || getDefaultForRole(profile.role);
      setDefaultDashboard(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  const loadFamilyInfo = async () => {
    try {
      setLoading(true);
      const info = {};

      // For players: get parent/guardian info
      if (profile.role === 'player') {
        info.isDependent = profile.is_dependent;

        // Get parent if dependent
        if (profile.managed_by) {
          const { data: parentData } = await supabase
            .from('profiles')
            .select('full_name, email')
            .eq('id', profile.managed_by)
            .single();
          info.parent = parentData;
        }

        // Get family info if has family_id
        if (profile.family_id) {
          const { data: familyMembers } = await supabase
            .from('profiles')
            .select('full_name, role')
            .eq('family_id', profile.family_id)
            .neq('id', profile.id);
          info.familyMembers = familyMembers || [];
        }
      }

      // For parents: get children info
      if (profile.role === 'parent') {
        let query = supabase.from('profiles').select('full_name, is_dependent, email');

        if (profile.family_id) {
          query = query.or(
            `and(family_id.eq.${profile.family_id},family_id.not.is.null),managed_by.eq.${profile.id}`
          );
        } else {
          query = query.eq('managed_by', profile.id);
        }

        const { data: childrenData } = await query.neq('id', profile.id).order('full_name');

        info.children = childrenData || [];
      }

      setFamilyInfo(info);
    } catch (error) {
      console.error('Error loading family info:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDefaultForRole = (role) => {
    switch (role) {
      case 'player':
        return 'player';
      case 'parent':
        return 'parent';
      case 'coach':
        return 'coach';
      case 'admin':
      case 'super_admin':
        return 'admin';
      default:
        return 'player';
    }
  };

  const getAvailableDashboards = () => {
    const dashboards = [];
    const role = profile?.role;

    if (['player', 'admin', 'super_admin'].includes(role)) {
      dashboards.push({ value: 'player', label: 'Player Dashboard' });
    }
    if (['parent', 'admin', 'super_admin'].includes(role)) {
      dashboards.push({ value: 'parent', label: 'Parent Dashboard' });
    }
    if (['coach', 'admin', 'super_admin'].includes(role)) {
      dashboards.push({ value: 'coach', label: 'Coach Dashboard' });
    }
    if (['admin', 'super_admin'].includes(role)) {
      dashboards.push({ value: 'admin', label: 'Admin Dashboard' });
    }

    return dashboards;
  };

  const handleSavePreference = async () => {
    try {
      setSavingPreference(true);
      setPreferenceSuccess('');

      const { error } = await supabase
        .from('profiles')
        .update({ default_dashboard: defaultDashboard })
        .eq('id', profile.id);

      if (error) throw error;

      setPreferenceSuccess('Default dashboard preference saved!');
      setTimeout(() => setPreferenceSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving preference:', error);
      alert('Failed to save preference: ' + error.message);
    } finally {
      setSavingPreference(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
    },
    header: {
      backgroundColor: 'white',
      borderBottom: '1px solid #e0e0e0',
      padding: isDesktop ? '20px 40px' : '16px',
      display: 'flex',
      flexDirection: isDesktop ? 'row' : 'column',
      justifyContent: 'space-between',
      alignItems: isDesktop ? 'center' : 'stretch',
      gap: isDesktop ? '0' : '12px',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    headerLogo: {
      height: isDesktop ? '56px' : '45px',
      width: isDesktop ? 'auto' : '45px',
      objectFit: 'contain',
    },
    mobileHeaderContainer: {
      display: 'grid',
      gridTemplateColumns: '45px 1fr 45px',
      alignItems: 'center',
      width: '100%',
      gap: '12px',
    },
    mobileHeaderTitle: {
      textAlign: 'center',
      overflow: 'visible',
    },
    title: {
      fontSize: isDesktop ? '24px' : '20px',
      fontWeight: 'bold',
      color: '#333',
      margin: 0,
    },
    headerButtons: {
      display: 'flex',
      flexDirection: isDesktop ? 'row' : 'column',
      gap: '10px',
    },
    button: {
      padding: isDesktop ? '10px 20px' : '14px 20px',
      fontSize: '14px',
      fontWeight: '500',
      color: 'white',
      backgroundColor: '#dc3545',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      minHeight: '44px',
    },
    adminButton: {
      padding: isDesktop ? '10px 20px' : '14px 20px',
      fontSize: '14px',
      fontWeight: '500',
      color: 'white',
      backgroundColor: '#007bff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      minHeight: '44px',
    },
    content: {
      padding: isDesktop ? '40px' : '16px',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      padding: isDesktop ? '30px' : '16px',
      marginBottom: isDesktop ? '20px' : '12px',
    },
    cardTitle: {
      fontSize: '20px',
      fontWeight: 'bold',
      color: '#333',
      marginBottom: '20px',
    },
    infoGrid: {
      display: 'grid',
      gap: '15px',
    },
    infoRow: {
      display: 'flex',
      flexDirection: isDesktop ? 'row' : 'column',
      borderBottom: '1px solid #f0f0f0',
      padding: '10px 0',
      gap: isDesktop ? '0' : '4px',
    },
    label: {
      fontWeight: '600',
      color: '#666',
      minWidth: isDesktop ? '150px' : '0',
      fontSize: isDesktop ? '14px' : '13px',
    },
    value: {
      color: '#333',
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
      fontSize: isDesktop ? '14px' : '14px',
    },
    badge: {
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '500',
      textTransform: 'uppercase',
    },
    comingSoon: {
      color: '#999',
      fontStyle: 'italic',
    },
    backLink: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      color: '#007bff',
      textDecoration: 'none',
      fontSize: '14px',
      fontWeight: '500',
      padding: isDesktop ? '16px 40px' : '12px 16px',
      cursor: 'pointer',
      backgroundColor: 'transparent',
      border: 'none',
      transition: 'color 0.2s',
    },
    successMessage: {
      padding: '12px',
      backgroundColor: '#d4edda',
      color: '#155724',
      borderRadius: '4px',
      marginBottom: '16px',
      border: '1px solid #c3e6cb',
    },
  };

  const getRoleBadgeStyle = (role) => {
    const colors = {
      admin: { bg: '#e3f2fd', color: '#1976d2' },
      coach: { bg: '#f3e5f5', color: '#7b1fa2' },
      player: { bg: '#e8f5e9', color: '#388e3c' },
      parent: { bg: '#fff3e0', color: '#f57c00' },
      super_admin: { bg: '#f3e5f5', color: '#4a148c' },
    };
    const style = colors[role] || colors.player;
    return {
      ...styles.badge,
      backgroundColor: style.bg,
      color: style.color,
    };
  };

  return (
    <div style={styles.container}>
      <ImpersonationBanner />
      <header style={styles.header}>
        {isDesktop ? (
          <>
            <div style={styles.headerLeft}>
              <img src="/logo.png" alt="Reps Over Rings" style={styles.headerLogo} />
              <h1 style={styles.title}>Account Information</h1>
            </div>
            <DashboardNavigation />
          </>
        ) : (
          <div style={styles.mobileHeaderContainer}>
            <img src="/logo.png" alt="Reps Over Rings" style={styles.headerLogo} />
            <div style={styles.mobileHeaderTitle}>
              <h1 style={styles.title}>Account Information</h1>
            </div>
            <AvatarMenu />
          </div>
        )}
      </header>

      {!isDesktop && (
        <button
          style={styles.backLink}
          onClick={() => {
            const defaultDash = getDefaultDashboard();
            navigate(`/${defaultDash}`);
          }}
        >
          <span>←</span>
          <span>Back to Dashboard</span>
        </button>
      )}

      <div style={styles.content}>
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Your Profile</h2>
          <div style={styles.infoGrid}>
            <div style={styles.infoRow}>
              <span style={styles.label}>Email:</span>
              <span style={styles.value}>{profile?.email}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.label}>User ID:</span>
              <span style={styles.value}>{profile?.id}</span>
            </div>
            {profile && (
              <>
                <div style={styles.infoRow}>
                  <span style={styles.label}>Full Name:</span>
                  <span style={styles.value}>{profile.full_name || 'Not set'}</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.label}>Role:</span>
                  <span style={getRoleBadgeStyle(profile.role)}>{profile.role || 'Not set'}</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.label}>Organization:</span>
                  <span style={styles.value}>{profile.organization_id || 'Not assigned'}</span>
                </div>

                {/* Family Information for Players */}
                {profile.role === 'player' && familyInfo && (
                  <>
                    <div style={styles.infoRow}>
                      <span style={styles.label}>Account Type:</span>
                      <span style={styles.value}>
                        {familyInfo.isDependent ? 'Dependent (Managed)' : 'Independent'}
                      </span>
                    </div>
                    {familyInfo.parent && (
                      <div style={styles.infoRow}>
                        <span style={styles.label}>Managed By:</span>
                        <span style={styles.value}>
                          {familyInfo.parent.full_name} ({familyInfo.parent.email})
                        </span>
                      </div>
                    )}
                    {profile.family_id && (
                      <>
                        <div style={styles.infoRow}>
                          <span style={styles.label}>Family ID:</span>
                          <span style={styles.value}>{profile.family_id}</span>
                        </div>
                        {familyInfo.familyMembers && familyInfo.familyMembers.length > 0 && (
                          <div style={styles.infoRow}>
                            <span style={styles.label}>Family Members:</span>
                            <span style={styles.value}>
                              {familyInfo.familyMembers
                                .map((member) => member.full_name)
                                .join(', ')}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}

                {/* Family Information for Parents */}
                {profile.role === 'parent' && familyInfo && (
                  <>
                    {profile.family_id && (
                      <div style={styles.infoRow}>
                        <span style={styles.label}>Family ID:</span>
                        <span style={styles.value}>{profile.family_id}</span>
                      </div>
                    )}
                    {familyInfo.children && familyInfo.children.length > 0 && (
                      <div style={styles.infoRow}>
                        <span style={styles.label}>Linked Players:</span>
                        <span style={styles.value}>
                          {familyInfo.children.map((child) => (
                            <div key={child.email || child.full_name}>
                              {child.full_name} {child.is_dependent && '(Managed)'}
                              {!child.is_dependent && '(Independent)'}
                            </div>
                          ))}
                        </span>
                      </div>
                    )}
                    {(!familyInfo.children || familyInfo.children.length === 0) && (
                      <div style={styles.infoRow}>
                        <span style={styles.label}>Linked Players:</span>
                        <span style={styles.value}>No players linked yet</span>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
            {!profile && (
              <div style={styles.infoRow}>
                <span style={styles.label}>Profile:</span>
                <span style={{ ...styles.value, ...styles.comingSoon }}>
                  Profile not yet created. Please contact your administrator.
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Default Dashboard Preference - Only show if user has multiple dashboards */}
        {profile && getAvailableDashboards().length > 1 && (
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Default Dashboard Preference</h2>
            <p style={{ marginBottom: '16px', color: '#666' }}>
              Choose which dashboard you want to see when you log in.
            </p>

            {preferenceSuccess && <div style={styles.successMessage}>{preferenceSuccess}</div>}

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Default Dashboard:
              </label>
              <select
                value={defaultDashboard}
                onChange={(e) => setDefaultDashboard(e.target.value)}
                style={{
                  width: '100%',
                  maxWidth: '300px',
                  minHeight: '44px',
                  padding: '8px 12px',
                  fontSize: '16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                }}
              >
                {getAvailableDashboards().map((dash) => (
                  <option key={dash.value} value={dash.value}>
                    {dash.label}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={handleSavePreference}
              disabled={savingPreference}
              style={{
                minHeight: '44px',
                padding: '10px 24px',
                fontSize: '16px',
                fontWeight: '500',
                color: 'white',
                backgroundColor: '#007bff',
                border: 'none',
                borderRadius: '4px',
                cursor: savingPreference ? 'not-allowed' : 'pointer',
                opacity: savingPreference ? 0.6 : 1,
              }}
            >
              {savingPreference ? 'Saving...' : 'Save Preference'}
            </button>
          </div>
        )}

        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Quick Stats</h2>
          <p style={styles.comingSoon}>Dashboard features coming soon...</p>
          <ul>
            <li>View your team's performance metrics</li>
            <li>Track assigned drills and progress</li>
            <li>Monitor player development stats</li>
            <li>Access training videos and resources</li>
          </ul>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AccountInfoPage;
